/**
 * Кнопка «Сбросить» в меню фильтра столбца (CAB-651).
 *
 * Жалоба из заявки 2394: в таблице десятки столбцов, включённые фильтры видно
 * только по подсвеченной воронке в шапке, и чтобы вернуться к полному списку,
 * человек обходит столбцы по одному. Кнопка снимает все фильтры таблицы за один
 * раз и стоит там, где пользователь уже находится в этот момент — в самом меню
 * фильтра, левым краем той же строки, где «ОК» и «Закрыть».
 *
 * Решение общее, а не по гридам: меню фильтра рисует один и тот же вид
 * `headerFilterView` из `grid_core`, поэтому расширяем его один раз на старте
 * приложения — через `registerModule`, штатный механизм расширения гридов
 * DevExtreme (`grid_core/m_modules.js`). Так кнопка появляется во всех таблицах
 * и деревьях кабинета, включая те, которых ещё нет, и ни один грид не нужно
 * править пропсами.
 *
 * Расширитель вида задаётся функцией `Base => class extends Base`, а не
 * объектом: виды в 24.1 — обычные ES-классы, у них нет `inherit`/`callBase`
 * старого `Class` DevExtreme (см. `getExtendedTypes` в `m_modules.js`).
 *
 * Кнопка всегда активна. Гасить её, когда применённых фильтров нет, оказалось
 * плохой идеей: галочки, поставленные в открытом меню, фильтром становятся
 * только после «ОК», и человек видел выбранные значения рядом с серой кнопкой.
 * Нажатие при пустой таблице ничего не портит — DevExtreme не перезагружает
 * данные, когда снимать нечего.
 *
 * Меню после нажатия остаётся открытым: человек видит, как гаснут воронки, и
 * может тут же отфильтровать столбец заново, не открывая меню второй раз. За это
 * приходится приводить в порядок и внутреннее состояние меню — оно собрано при
 * открытии и о сбросе само не узнает:
 *
 * # отметки в списке значений (`getListComponent().unselectAll()`);
 * # отложенный выбор `options.filterValues`/`filterType`, который «ОК» пишет в
 *   столбец (`applyHeaderFilter` в `header_filter/m_header_filter_core.js`);
 * # сам список значений: он загружен с фильтром остальных столбцов, снятым при
 *   открытии меню (`getDataSource` в `header_filter/m_header_filter.js`), и после
 *   сброса показывал бы только значения из прежней, урезанной выборки.
 *
 * Без второго пункта снятие галочек ничего не решало: список выглядел пустым, а
 * «ОК» возвращал только что снятый фильтр — проверено в браузере.
 *
 * Что именно сбрасываем — фильтры столбцов (строка фильтра, галочки меню) и
 * значение панели фильтра. Штатный `clearFilter()` без аргументов трогать
 * нельзя: он вдобавок снимает фильтр источника данных и текст поиска, а
 * фильтром источника кабинет ограничивает выборку по делу (вкладки плагинов,
 * история проценок) — сброс из меню столбца показал бы строки, которых в этом
 * разделе быть не должно.
 */

import DataGrid from "devextreme/ui/data_grid";
import TreeList from "devextreme/ui/tree_list";
import i18n from "../../../../i18n";

/** Класс кнопки: по нему её находят стили и тесты. */
export const HEADER_FILTER_RESET_CLASS = "header-filter-reset";

const MODULE_NAME = "headerFilterReset";

/** Грид (DataGrid/TreeList) в объёме, нужном кнопке. */
export interface FilterableGrid {
  option(name: string): unknown;
  clearFilter(filterName: string): void;
  beginUpdate(): void;
  endUpdate(): void;
}

/** Элемент нижней панели меню фильтра — в терминах `toolbarItems` Popup. */
interface PopupToolbarItem {
  toolbar?: string;
  location?: string;
  widget?: string;
  options?: { elementAttr?: { class?: string } } & Record<string, unknown>;
}

/** Меню фильтра: Popup, у которого мы дополняем нижнюю панель. */
interface HeaderFilterPopup {
  option(name: string): unknown;
  option(name: string, value: unknown): void;
}

/** Источник значений списка — обычный DataSource DevExtreme. */
interface HeaderFilterListDataSource {
  filter?(value: unknown): void;
  reload?(): void;
}

/** Список значений в открытом меню: и плоский (List), и дерево (TreeView). */
interface HeaderFilterList {
  unselectAll?(): void;
  getDataSource?(): HeaderFilterListDataSource | undefined;
}

/** Вид меню фильтра из `grid_core` в объёме, нужном расширителю. */
export interface HeaderFilterViewLike {
  component: FilterableGrid;
  _popupContainer?: HeaderFilterPopup;
  getListComponent?(): HeaderFilterList | undefined;
}

/**
 * Опции открытого меню. `filterValues`/`filterType` — то, что «ОК» применит к
 * столбцу; DevExtreme копирует их из столбца при открытии.
 */
export interface HeaderFilterMenuOptions {
  isFilterBuilder?: boolean;
  filterValues?: unknown;
  filterType?: string;
}

/** Снимает фильтры столбцов и панели фильтра, не касаясь выборки раздела и поиска. */
export function resetGridFilters(grid: FilterableGrid): void {
  grid.beginUpdate();
  try {
    grid.clearFilter("row");
    grid.clearFilter("header");
    if (grid.option("filterValue") != null) grid.clearFilter("filterValue");
  } finally {
    grid.endUpdate();
  }
}

/** Приводит открытое меню к виду «фильтров нет»: список, его значения и то, что применит «ОК». */
function clearMenuState(view: HeaderFilterViewLike, options?: HeaderFilterMenuOptions): void {
  const list = view.getListComponent?.();

  list?.unselectAll?.();

  if (options) {
    options.filterValues = null;
    options.filterType = "include";
  }

  // без фильтра остальных столбцов список значений снова полный; отметки в нём
  // DevExtreme проставляет из `filterValues` столбца, а они уже сняты
  const dataSource = list?.getDataSource?.();
  dataSource?.filter?.(null);
  dataSource?.reload?.();
}

function isResetItem(item: PopupToolbarItem): boolean {
  return item.options?.elementAttr?.class === HEADER_FILTER_RESET_CLASS;
}

function createResetItem(view: HeaderFilterViewLike, options?: HeaderFilterMenuOptions): PopupToolbarItem {
  const grid = view.component;

  return {
    toolbar: "bottom",
    location: "before",
    widget: "dxButton",
    options: {
      text: i18n.global.t("headerFilter.resetAll"),
      hint: i18n.global.t("headerFilter.resetAllHint"),
      elementAttr: { class: HEADER_FILTER_RESET_CLASS },
      onClick(): void {
        resetGridFilters(grid);
        clearMenuState(view, options);
      },
    },
  };
}

/**
 * Добавляет кнопку в левый край нижней панели уже собранного меню фильтра.
 *
 * Меню пересобирается при каждом открытии, поэтому свою кнопку сначала
 * выкидываем: если DevExtreme однажды перестанет пересоздавать `toolbarItems`,
 * кнопки не начнут копиться.
 */
export function addResetButtonToPopup(view: HeaderFilterViewLike, options?: HeaderFilterMenuOptions): void {
  // меню конструктора фильтра живёт в панели фильтра и фильтрами столбцов не управляет
  if (options?.isFilterBuilder) return;

  const popup = view._popupContainer;
  if (!popup) return;

  const current = popup.option("toolbarItems");
  const items = Array.isArray(current) ? (current as PopupToolbarItem[]) : [];

  popup.option("toolbarItems", [createResetItem(view, options), ...items.filter((item) => !isResetItem(item))]);
}

/** Класс вида меню фильтра. `any[]` — требование TS к базовому классу миксина. */
type HeaderFilterViewClass = new (...args: any[]) => HeaderFilterViewLike & {
  _initializePopupContainer(options: HeaderFilterMenuOptions): void;
};

/** Виджет грида в объёме статики регистрации модулей (в типах DevExtreme её нет). */
interface GridModuleRegistry {
  registerModule(name: string, module: unknown): void;
}

export function applyDxHeaderFilterReset(): void {
  const module = {
    extenders: {
      views: {
        headerFilterView: (Base: HeaderFilterViewClass) =>
          class HeaderFilterViewWithReset extends Base {
            _initializePopupContainer(options: HeaderFilterMenuOptions): void {
              super._initializePopupContainer(options);
              addResetButtonToPopup(this, options);
            }
          },
      },
    },
  };

  (DataGrid as unknown as GridModuleRegistry).registerModule(MODULE_NAME, module);
  (TreeList as unknown as GridModuleRegistry).registerModule(MODULE_NAME, module);
}
